import type { CSSProp } from 'styled-components';
import styled from 'styled-components';

import { Card } from '@xing-com/card';
import { lineClamp } from '@xing-com/crate-utils-line-clamp';
import { mediaSmallOrTiny } from '@xing-com/layout-tokens';
import {
  SkeletonHeadline as SkeletonHeadlineComponent,
  SkeletonBody as SkeletonBodyComponent,
  SkeletonButton as SkeletonButtonComponent,
  SkeletonProfileImage as SkeletonProfileImageComponent,
} from '@xing-com/skeleton';
import { SuperEllipse as SuperEllipseComponent } from '@xing-com/super-ellipse';
import {
  scale190,
  scale220,
  spaceL,
  spaceM,
  spaceS,
  spaceXL,
  spaceXS,
  spaceXXL,
} from '@xing-com/tokens';
import {
  BodyCopy as BodyCopyComponent,
  Headline as HeadlineComponent,
} from '@xing-com/typography';

const showLineClamp = (showMoreThanOneLine: boolean): CSSProp => {
  const numberOfLines = showMoreThanOneLine ? 2 : 1;

  return lineClamp(numberOfLines);
};

export const CardContainer = styled(Card)`
  align-items: center;
  padding: ${spaceXXL} ${spaceL};
  text-align: center;
  height: 100%;

  & > div {
    height: 100%;
  }

  @media ${mediaSmallOrTiny} {
    flex-direction: row;
    padding: ${spaceL};
    text-align: left;

    & > div {
      flex: 1 1 auto;
      flex-direction: row;
      gap: ${spaceL};
    }
  }
`;

export const ImageContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: ${spaceXS} 0 ${spaceXL};

  @media ${mediaSmallOrTiny} {
    margin: 0;
  }
`;

export const SuperEllipse = styled(SuperEllipseComponent)`
  @media ${mediaSmallOrTiny} {
    width: ${scale190};
    height: ${scale190};
  }
`;

export const CardLink = styled.a`
  &:hover {
    text-decoration: none;
  }
`;

export const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  word-break: break-word;
`;

export const Headline = styled(HeadlineComponent)<{
  $showMoreThanOneLine: boolean;
}>`
  margin-bottom: ${spaceXS};
  ${({ $showMoreThanOneLine }) => showLineClamp($showMoreThanOneLine)};
`;

export const BodyCopy = styled(BodyCopyComponent)<{
  $showMoreThanOneLine: boolean;
}>`
  margin-bottom: ${spaceS};
  ${({ $showMoreThanOneLine }) => showLineClamp($showMoreThanOneLine)};
`;

export const ButtonContainer = styled.div`
  margin-top: auto;
  margin-bottom: ${spaceXS};

  @media ${mediaSmallOrTiny} {
    margin: 0;
  }
`;

export const SkeletonHeadline = styled(SkeletonHeadlineComponent)`
  margin-bottom: ${spaceS};
`;

export const SkeletonBody = styled(SkeletonBodyComponent)`
  margin-bottom: ${spaceM};

  @media ${mediaSmallOrTiny} {
    justify-content: left;
  }
`;

export const SkeletonButton = styled(SkeletonButtonComponent)`
  max-width: ${scale220};
`;

export const SkeletonProfileImage = styled(SkeletonProfileImageComponent)`
  @media ${mediaSmallOrTiny} {
    width: ${scale190};
    height: ${scale190};
  }
`;
